import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import BasicTable from "shared/ui/Table";
import { customerColumns, CustomerStatusObj } from "./constants";
import { Box } from "@mui/system";
import { MenuItem, Select, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { DEFAULT_CHANGE_ALL_STATUSES_PAYLOAD, defaultFilterValues } from "resources/constants";
import {
  ChangeCustomerStatus,
  ChangeCustomerStatusSelected,
  ChangeCustomerAllStatuses,
  ExportCustomers,
  GetCustomers,
  selectCustomers,
} from "store/slicers/customers";
import { IChangeCustomerAllStatuses, ICustomer } from "store/interfaces/customers";
import { ERequestStatus } from "store/enums/index.enum";
import Filters from "./components/Filters";
import { setTableLoading } from "store/slicers/common";
import usePermission from "shared/helpers/hooks/usePermission";
import { ECustomersPermissions } from "resources/permissions/permissions.enum";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { handleDownload } from "shared/helpers";
import StatusSelect from "pages/dashboard/Customers/components/StatusSelect";

export type TCustomerStatus = 1 | 2 | 3;

const Customers = () => {
  const dispatch = useAsyncDispatch();
  const customers = useSelector(selectCustomers);
  const t = useTranslation();

  const hasEditPermission = usePermission(ECustomersPermissions.Edit);
  const hasExportPermission = usePermission(ECustomersPermissions.Export);

  const [checkedCustomers, setCheckedCustomers] = useState([])
  const [showChangeStatus, setShowChangeStatus] = useState(false)

  const methods = useForm({
    defaultValues: { config: defaultFilterValues },
  });

  const filterValue = methods.watch("config.filters")?.[0]?.value;

  const refetchCustomers = useCallback(async () => {
    await dispatch(setTableLoading(true));
    const data = {
      ...methods.watch("config"),
      filters: methods.watch("config.filters").filter((i) => i),
    };
    await dispatch(GetCustomers(data));
    await dispatch(setTableLoading(false));
  }, [dispatch, methods]);

  const handleStatus = (targetStatus) => {
    const currentStatus = Number(filterValue);

    if (Number(currentStatus) === Number(targetStatus)) {
      alert('Error, duplicate status')
      return
    }

    // меняем статус всех выбранных
    if (checkedCustomers.length === customers.totalRecords) {
      handleChangeAllStatuses(targetStatus, currentStatus)
    } else if (checkedCustomers.length > 0) {
      // меняем статус только выбранных
      handleChangeStatusSelected(targetStatus, checkedCustomers);
    }

  }

  // сохраняем дефолтный paylod todo вынести  
  const buildChangeCustomerAllStatusesPayload = (
    targetStatus: TCustomerStatus,
    currentStatus: TCustomerStatus
  ): IChangeCustomerAllStatuses => ({
    ...DEFAULT_CHANGE_ALL_STATUSES_PAYLOAD,
    newState: targetStatus, // это статус, на который меняем
    grid: {
      ...DEFAULT_CHANGE_ALL_STATUSES_PAYLOAD.grid,
      filters: [
        {
          ...DEFAULT_CHANGE_ALL_STATUSES_PAYLOAD.grid.filters[0],
          value: String(currentStatus),
        },
      ],
    },
  });

  // todo сделать норм обработку 
  const handleStatusUpdate = async (action) => {
    const { meta } = await dispatch(action);

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      console.log(meta, 'some error')
      return;
    } else {
      window.location.reload()
    }

  };

  const handleChangeStatus = useCallback(
    (e, rowId) =>
      handleStatusUpdate(
        ChangeCustomerStatus({
          id: rowId,
          formData: { state: e.target.value },
        })
      ),
    [dispatch, refetchCustomers]
  );

  const handleChangeStatusSelected = useCallback(
    (targetStatus: TCustomerStatus, selectedIds: number[]) => {
      const payload = {
        newState: targetStatus,
        selectedIds,
      };

      handleStatusUpdate(ChangeCustomerStatusSelected(payload));
    },
    [handleStatusUpdate]
  );

  const handleChangeAllStatuses = useCallback(
    (targetStatus, currentStatus) => {
      handleStatusUpdate(
        ChangeCustomerAllStatuses(
          buildChangeCustomerAllStatusesPayload(targetStatus, currentStatus)
        )
      );
    },
    [handleStatusUpdate]
  );



  const statusColumn = useMemo(() => {
    return {
      label: "status",
      layout: (row: ICustomer) => {
        return (
          <Box textAlign="center">
            <Select
              disabled={!hasEditPermission}
              size="small"
              value={row.customerStatus.id}
              onChange={(e) => handleChangeStatus(e, row.id)}
            >
              <MenuItem disabled={row.customerStatus.id === 1} value={1}>
                {t("active")}
              </MenuItem>
              <MenuItem disabled={row.customerStatus.id === 2} value={2}>
                {t("blocked")}
              </MenuItem>
              <MenuItem disabled value={3}>
                {t("quarantined")}
              </MenuItem>
            </Select>
            {row.customerStatus.id === 3 && (
              <Typography mt={0.4} fontSize={12}>
                {t("until")} {row.quarantineEndDate}
              </Typography>
            )}
          </Box>
        );
      },
    };
  }, [t, hasEditPermission, handleChangeStatus]);

  const handleExport = async (selected: number[]) => {
    const { meta, payload } = await dispatch(
      ExportCustomers({ customerIDs: selected })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    handleDownload(payload);
  };

  const handleChangeSelected = (ids: number[]) => {
    setCheckedCustomers(ids);
  };

  useEffect(() => {
    if (filterValue) {
      setShowChangeStatus(filterValue && checkedCustomers.length > 0);
    }
  }, [filterValue, checkedCustomers]);

  const init = useCallback(async () => {
    await dispatch(setTableLoading(true));
    await dispatch(GetCustomers(defaultFilterValues));
    await dispatch(setTableLoading(false));
  }, [dispatch]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <Box p={4}>
      <Typography variant="h4" fontWeight={500} color="text.secondary">
        {t("customers_section_title")}
      </Typography>
      {showChangeStatus && (
        <Box
          px={2}
          display="flex"
          justifyContent="flex-end"
        >
          <StatusSelect onSave={handleStatus} />
        </Box>
      )}
      <BasicTable<ICustomer>
        selectable={hasExportPermission}
        hasSearchInput
        onExport={handleExport}
        title={`${checkedCustomers.length} ${t("selected")} ${CustomerStatusObj[filterValue] ? CustomerStatusObj[filterValue] : ''}`}
        Filter={() => <Filters onChange={refetchCustomers} methods={methods} />}
        filterOptions={{ watch: methods.watch, reset: methods.reset }}
        columns={[...customerColumns, statusColumn]}
        paginatedData={customers}
        onChange={refetchCustomers}
        onChangeSelected={handleChangeSelected}
      />
    </Box>
  );
};

export default Customers;
